import { useField, useFormikContext } from "formik";
import { useState, useRef, useEffect } from "react";

const OrganizationSelectInput = ({
  label,
  data = [],
  onChange,
  value,
  labelClassName,
  onSearch,
  ...props
}) => {
  const [field, meta] = useField(props);
  const { values, setFieldValue } = useFormikContext(); // Access full Formik values
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(""); // ✅ store display name
  const dropdownRef = useRef(null);
  const isError = meta.touched && meta.error;

  // ✅ Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Debounced API call for organization search
  useEffect(() => {
    if (!isOpen || !onSearch) return;
    const trimmed = searchTerm.trim();
    if (trimmed === "") return;

    setIsTyping(true);
    const delayDebounce = setTimeout(() => {
      onSearch(trimmed);
      setIsTyping(false);
    }, 600);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // ✅ Local filtering when new data arrives
  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data);
    } else {
      setFilteredData(
        data.filter((item) =>
          item.organizationName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [data, searchTerm]);

  // ✅ If form value (id) changes externally, update displayed name
  useEffect(() => {
    if (field.value && data.length > 0) {
      const selected = data.find(
        (item) => item.organizationId === field.value
      );
      if (selected) setSelectedOrgName(selected.organizationName);
    }
  }, [field.value, data]);

  const handleSelect = (item) => {
    console.log("Selected organization:", item);
    const value = item.organizationId;
    const name = item.organizationName;

    // ✅ Store orgId in Formik
    if (props.form?.setFieldValue) {
      props.form.setFieldValue(props.name, value);
    } else {
      field.onChange({ target: { name: props.name, value } });
    }

    // ✅ Keep orgName for display
    setSelectedOrgName(name);
    if (onChange) onChange({ target: { value } });

    setIsOpen(false);
    setSearchTerm("");
  };

 const displayName = values?.[props.name]?.name || selectedOrgName;

  return (
    <div className="flex flex-col">
      <label
        className={`p-1 mt-2 text-gray-700 flex gap-1 ${labelClassName || 'text-lg'}`}
        htmlFor={props.id || props.name}
      >
        {label}
        {isError && (
          <div className="text-red-500 text-[12px]">*</div>
        )}
      </label>

      <div className="flex flex-col relative" ref={dropdownRef}>
        <div
          className={`p-2 text-gray-700 border rounded-lg cursor-pointer bg-white ${isError ? "" : "border-gray-200"
            }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="block w-full text-gray-600 text-[14px] truncate">
            {displayName}
          </span>

        </div>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {/* 🔍 Search Input */}
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
              <input
                type="text"
                autoFocus
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 placeholder-gray-400 placeholder:text-[14px]"
              />
            </div>

            {/* Options */}
            {filteredData.length > 0 ? (
              filteredData.map((item, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                  onClick={() => handleSelect(item)}
                >
                  {item.organizationName}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 text-sm">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationSelectInput;
